import { apiClient } from "./client";
import {
  compareInstantsAtMicrosecondPrecision,
  normalizeInstantToUtcMicroseconds,
} from "../lib/temporalInstant";

export const HUMAN_PROVENANCE_CONTRACT_VERSION = "thread.human-provenance.v1" as const;
export const HUMAN_PROVENANCE_RESOURCE_LIMITS_VERSION =
  "thread.human-provenance-resource-limits.v1" as const;
export const HUMAN_PROVENANCE_MIN_SCHEMA_MIGRATION = "20260811120000" as const;
export const HUMAN_PROVENANCE_PATH = "/thread/v1/provenance/human/query" as const;

export const HUMAN_PROVENANCE_LIMITS = {
  maxPageSize: 100,
  maxFilterValues: 50,
  maxBuckets: 100,
  maxEvidence: 1_000,
  maxStatusEvents: 500,
  maxDocumentCanonicalBytes: 8_388_608,
} as const;

export type HumanProjection = "snapshot" | "search" | "related" | "focus" | "facet";
export type HumanRecordKind = "decision" | "decision_evidence" | "journal_entry";
export type HumanGroupDimension =
  | "record_kind"
  | "actor"
  | "decision_status"
  | "evidence_type"
  | "entry_type"
  | "sensitivity_tier"
  | "lifecycle_state";
export type HumanDecisionStatus =
  | "proposed"
  | "ratified"
  | "under_review"
  | "pending_ratification"
  | "withdrawn"
  | "superseded";
export type HumanLifecycleState =
  | "active"
  | "invalidated"
  | "superseded"
  | "redacted"
  | "abandoned"
  | "inapplicable";
export type HumanWarning =
  | "human_provenance_page_truncated"
  | "temporal_history_incomplete";

export interface HumanArtifactVersionRef {
  entity_kind: "artifact_version";
  id: string;
}

export interface HumanDecisionRef {
  entity_kind: "decision";
  id: string;
}

export interface HumanJournalRef {
  entity_kind: "journal_entry";
  id: string;
}

export interface HumanDecisionEvidenceRef {
  entity_kind: "decision_evidence";
  decision_id: string;
  artifact_version_id: string;
}

export interface HumanDecisionStatusEventRef {
  entity_kind: "decision_status_event";
  id: string;
}

export interface HumanOperationRunRef {
  entity_kind: "operation_run";
  id: string;
}

export type HumanRootRef =
  | HumanArtifactVersionRef
  | HumanDecisionRef
  | HumanJournalRef
  | HumanDecisionEvidenceRef;

export interface HumanProvenanceFilters {
  actor_ids?: string[];
  decision_statuses?: HumanDecisionStatus[];
  evidence_types?: string[];
  entry_types?: string[];
  sensitivity_tier_ids?: string[];
  lifecycle_states?: HumanLifecycleState[];
  effective_from?: string | null;
  effective_through?: string | null;
}

export interface HumanProvenanceRequest {
  project_id: string;
  projection: HumanProjection;
  root_ref?: HumanRootRef | null;
  record_kinds?: HumanRecordKind[];
  query?: string | null;
  filters?: HumanProvenanceFilters;
  group_by?: HumanGroupDimension | null;
  sort?: "effective_at_desc";
  page_size?: number;
  cursor?: string | null;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
}

export interface HumanAppliedFilters {
  actor_ids: string[];
  decision_statuses: HumanDecisionStatus[];
  evidence_types: string[];
  entry_types: string[];
  sensitivity_tier_ids: string[];
  lifecycle_states: HumanLifecycleState[];
  effective_from: string | null;
  effective_through: string | null;
}

export interface HumanAppliedSelection {
  record_kinds: HumanRecordKind[];
  query: string | null;
  filters: HumanAppliedFilters;
  group_by: HumanGroupDimension | null;
  sort: "effective_at_desc";
  page_size: number;
}

export interface NormalizedHumanProvenanceRequest {
  project_id: string;
  projection: HumanProjection;
  root_ref: HumanRootRef | null;
  record_kinds: HumanRecordKind[];
  query: string | null;
  filters: HumanAppliedFilters;
  group_by: HumanGroupDimension | null;
  sort: "effective_at_desc";
  page_size: number;
  cursor: string | null;
  as_of: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
}

export interface HumanSensitivityView {
  basis: "technical_claim_artifact" | "evidence_artifact" | "journal_content_artifact";
  tier_id: string;
  tier_name: string;
}

export interface HumanValidityView {
  basis:
    | "technical_claim_version"
    | "evidence_artifact_version"
    | "journal_content_artifact_version";
  lifecycle_state: HumanLifecycleState;
  considered_valid_at_time: boolean;
  authoritative_for_new_work_at_time: boolean;
}

export interface HumanEvidenceType {
  basis: "artifact_kind";
  value: string;
}

export interface HumanDecisionCard {
  entity_kind: "decision";
  ref: HumanDecisionRef;
  project_id: string;
  decision_key: string;
  technical_claim_ref: HumanArtifactVersionRef;
  technical_claim_name: string;
  current_status: HumanDecisionStatus;
  current_status_event_ref: HumanDecisionStatusEventRef;
  creator_actor_id: string;
  sensitivity: HumanSensitivityView & { basis: "technical_claim_artifact" };
  validity: HumanValidityView & { basis: "technical_claim_version" };
  effective_at: string;
  recorded_at: string;
}

export interface HumanEvidenceCard {
  entity_kind: "decision_evidence";
  ref: HumanDecisionEvidenceRef;
  project_id: string;
  decision_ref: HumanDecisionRef;
  evidence_artifact_version_ref: HumanArtifactVersionRef;
  evidence_name: string;
  decision_status: HumanDecisionStatus;
  evidence_type: HumanEvidenceType;
  creator_actor_id: string;
  sensitivity: HumanSensitivityView & { basis: "evidence_artifact" };
  validity: HumanValidityView & { basis: "evidence_artifact_version" };
  effective_at: string;
  recorded_at: string;
}

export interface HumanJournalCard {
  entity_kind: "journal_entry";
  ref: HumanJournalRef;
  project_id: string;
  subject_ref: HumanArtifactVersionRef;
  content_artifact_version_ref: HumanArtifactVersionRef;
  content_name: string;
  decision_ref: HumanDecisionRef | null;
  entry_type: string;
  author_actor_id: string;
  publication_operation_ref: HumanOperationRunRef;
  sensitivity: HumanSensitivityView & { basis: "journal_content_artifact" };
  validity: HumanValidityView & { basis: "journal_content_artifact_version" };
  effective_at: string;
  recorded_at: string;
}

export type HumanCard = HumanDecisionCard | HumanEvidenceCard | HumanJournalCard;

export interface HumanDecisionStatusEvent {
  entity_kind: "decision_status_event";
  ref: HumanDecisionStatusEventRef;
  decision_ref: HumanDecisionRef;
  status: HumanDecisionStatus;
  actor_id: string;
  effective_at: string;
  recorded_at: string;
}

export type HumanCompleteEvidenceItem = HumanEvidenceCard & { ordinal: number };
export interface HumanEvidenceOmissionMarker {
  status: "incomplete";
  marker: "restricted_evidence_omitted";
}

export type HumanDecisionEvidence =
  | { disclosure: "complete"; items: HumanCompleteEvidenceItem[]; marker: null }
  | { disclosure: "partial"; items: HumanEvidenceCard[]; marker: HumanEvidenceOmissionMarker };

export interface HumanDecisionDetail extends HumanDecisionCard {
  status_history: HumanDecisionStatusEvent[];
  evidence: HumanDecisionEvidence;
}

export interface HumanEvidenceDetail extends HumanEvidenceCard {
  parent_decision: HumanDecisionCard;
  parent_evidence_disclosure: "complete" | "partial";
  ordinal: number | null;
  marker: HumanEvidenceOmissionMarker | null;
}

export interface HumanJournalDetail extends HumanJournalCard {
  related_decision: HumanDecisionCard | null;
}

export type HumanDetail = HumanDecisionDetail | HumanEvidenceDetail | HumanJournalDetail;

export interface HumanFacetBucket {
  dimension: HumanGroupDimension;
  key: string;
  label: string;
  count: number;
}

export type HumanTemporalHistory =
  | { status: "complete"; marker: null }
  | { status: "incomplete"; marker: "temporal_history_incomplete" };

export interface HumanProvenanceResult {
  contract_version: typeof HUMAN_PROVENANCE_CONTRACT_VERSION;
  resource_limits_version: typeof HUMAN_PROVENANCE_RESOURCE_LIMITS_VERSION;
  project_id: string;
  projection: HumanProjection;
  temporal_cut: {
    effective_as_of: string;
    recorded_as_of: string;
    snapshot_token: string;
  };
  root_ref: HumanRootRef | null;
  applied_selection: HumanAppliedSelection;
  items: HumanCard[];
  detail: HumanDetail | null;
  buckets: HumanFacetBucket[];
  next_cursor: string | null;
  truncated: boolean;
  temporal_history: HumanTemporalHistory;
  warnings: HumanWarning[];
}

export interface HumanProvenanceEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: HumanProvenanceResult;
  page: { next_cursor: string | null; truncated: boolean };
  provenance: {
    source: "thread_core";
    projection_version: typeof HUMAN_PROVENANCE_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: HumanWarning[];
}

export class HumanProvenanceValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "HumanProvenanceValidationError";
    this.path = path;
  }
}

type UnknownRecord = Record<string, unknown>;

const DOCUMENT_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const REQUEST_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AWARE_TIMESTAMP_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|(([+-])(\d{2}):(\d{2})))$/;
const FIXED_UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const SNAPSHOT_TOKEN_PATTERN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const HUMAN_OUTPUT_TYPE_PATTERN = /^[a-z][a-z0-9_.-]{0,99}$/;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/u;

const PROJECTIONS = ["snapshot", "search", "related", "focus", "facet"] as const;
const RECORD_KINDS = ["decision", "decision_evidence", "journal_entry"] as const;
const GROUP_DIMENSIONS = [
  "record_kind", "actor", "decision_status", "evidence_type", "entry_type",
  "sensitivity_tier", "lifecycle_state",
] as const;
const DECISION_STATUSES = [
  "proposed", "ratified", "under_review", "pending_ratification", "withdrawn", "superseded",
] as const;
const LIFECYCLE_STATES = [
  "active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable",
] as const;
const WARNINGS = ["human_provenance_page_truncated", "temporal_history_incomplete"] as const;
const STATUS_RANK: Record<HumanDecisionStatus, number> = {
  proposed: 0, under_review: 1, pending_ratification: 2, ratified: 3, withdrawn: 4, superseded: 5,
};
const LIFECYCLE_RANK: Record<HumanLifecycleState, number> = {
  active: 0, invalidated: 1, superseded: 2, redacted: 3, abandoned: 4, inapplicable: 5,
};
const KIND_RANK: Record<HumanRecordKind, number> = {
  decision: 0, decision_evidence: 1, journal_entry: 2,
};

function fail(path: string, expectation: string): never {
  throw new HumanProvenanceValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  return value as UnknownRecord;
}

function exactRecord(
  value: unknown,
  path: string,
  keys: readonly string[],
  optionalKeys: readonly string[] = [],
): UnknownRecord {
  const item = record(value, path);
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required field");
  }
  const expected = new Set([...keys, ...optionalKeys]);
  for (const key in item) {
    if (Object.prototype.hasOwnProperty.call(item, key) && !expected.has(key)) {
      fail(`${path}.${key}`, "unknown field on a known contract object");
    }
  }
  return item;
}

function array(value: unknown, path: string, max = Number.MAX_SAFE_INTEGER, min = 0): unknown[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  if (value.length < min || value.length > max) fail(path, `expected between ${min} and ${max} items`);
  return value;
}

function string(value: unknown, path: string, min = 1, max = Number.MAX_SAFE_INTEGER): string {
  if (typeof value !== "string") fail(path, "expected a string");
  const length = [...value].length;
  if (length < min || length > max) fail(path, `expected between ${min} and ${max} characters`);
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function integer(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || (value as number) < min || (value as number) > max) {
    fail(path, `expected a safe integer between ${min} and ${max}`);
  }
  return value as number;
}

function literal<T extends string>(value: unknown, path: string, values: readonly T[]): T {
  if (typeof value !== "string" || !values.includes(value as T)) {
    fail(path, `expected one of ${values.join(", ")}`);
  }
  return value as T;
}

function requestUuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!REQUEST_UUID_PATTERN.test(result)) fail(path, "expected a canonically hyphenated UUID");
  return result;
}

function documentUuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!DOCUMENT_UUID_PATTERN.test(result)) fail(path, "expected a lowercase canonical UUID");
  return result;
}

function validAwareTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[11]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[12]);
  const boundaryYearUsesUtc = (year !== 1 && year !== 9999) || match[8] === "Z";
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const parsed = Date.parse(value);
  const utcYear = Number.isFinite(parsed) ? new Date(parsed).getUTCFullYear() : 0;
  return year >= 1 && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && boundaryYearUsesUtc && utcYear >= 1 && utcYear <= 9999;
}

function awareTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!AWARE_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected an RFC 3339 timestamp with a timezone offset");
  }
  return result;
}

function fixedUtcTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = string(value, path, 60, 4096);
  if (!SNAPSHOT_TOKEN_PATTERN.test(result)) fail(path, "expected a canonical ts1 snapshot token");
  return result;
}

function sameStrings(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function unique<T>(values: readonly T[], path: string): void {
  if (new Set(values).size !== values.length) fail(path, "values must be unique");
}

function normalizeUuidList(value: unknown, path: string): string[] {
  const values = array(value, path, HUMAN_PROVENANCE_LIMITS.maxFilterValues)
    .map((entry, index) => requestUuid(entry, `${path}[${index}]`).toLowerCase());
  unique(values, path);
  return values.sort();
}

function normalizeTextList(value: unknown, path: string): string[] {
  const values = array(value, path, HUMAN_PROVENANCE_LIMITS.maxFilterValues).map((entry, index) => {
    const cleaned = string(entry, `${path}[${index}]`, 1, 100).trim();
    if (!HUMAN_OUTPUT_TYPE_PATTERN.test(cleaned)) {
      fail(`${path}[${index}]`, "must use a canonical type identifier");
    }
    return cleaned;
  });
  unique(values, path);
  return values.sort();
}

function normalizeEnumList<T extends string>(
  value: unknown,
  path: string,
  values: readonly T[],
  rank: Record<T, number>,
): T[] {
  const result = array(value, path, values.length).map((entry, index) =>
    literal(entry, `${path}[${index}]`, values));
  unique(result, path);
  return result.sort((left, right) => rank[left] - rank[right]);
}

function normalizeFilters(value: unknown, path: string): HumanAppliedFilters {
  const item = exactRecord(value ?? {}, path, [], [
    "actor_ids", "decision_statuses", "evidence_types", "entry_types",
    "sensitivity_tier_ids", "lifecycle_states", "effective_from", "effective_through",
  ]);
  const effectiveFrom = item.effective_from == null
    ? null
    : normalizeInstantToUtcMicroseconds(awareTimestamp(item.effective_from, `${path}.effective_from`));
  const effectiveThrough = item.effective_through == null
    ? null
    : normalizeInstantToUtcMicroseconds(awareTimestamp(item.effective_through, `${path}.effective_through`));
  if (effectiveFrom && effectiveThrough
    && compareInstantsAtMicrosecondPrecision(effectiveFrom, effectiveThrough) > 0) {
    fail(path, "effective_from must not follow effective_through");
  }
  return {
    actor_ids: normalizeUuidList(item.actor_ids ?? [], `${path}.actor_ids`),
    decision_statuses: normalizeEnumList(
      item.decision_statuses ?? [], `${path}.decision_statuses`, DECISION_STATUSES, STATUS_RANK,
    ),
    evidence_types: normalizeTextList(item.evidence_types ?? [], `${path}.evidence_types`),
    entry_types: normalizeTextList(item.entry_types ?? [], `${path}.entry_types`),
    sensitivity_tier_ids: normalizeUuidList(
      item.sensitivity_tier_ids ?? [], `${path}.sensitivity_tier_ids`,
    ),
    lifecycle_states: normalizeEnumList(
      item.lifecycle_states ?? [], `${path}.lifecycle_states`, LIFECYCLE_STATES, LIFECYCLE_RANK,
    ),
    effective_from: effectiveFrom,
    effective_through: effectiveThrough,
  };
}

function filtersAreEmpty(filters: HumanAppliedFilters): boolean {
  return !filters.actor_ids.length && !filters.decision_statuses.length
    && !filters.evidence_types.length && !filters.entry_types.length
    && !filters.sensitivity_tier_ids.length && !filters.lifecycle_states.length
    && filters.effective_from === null && filters.effective_through === null;
}

function parseRootRef(value: unknown, path: string, request = false): HumanRootRef {
  const discriminant = record(value, path).entity_kind;
  const uuidParser = request ? requestUuid : documentUuid;
  if (discriminant === "decision_evidence") {
    const item = exactRecord(value, path, ["entity_kind", "decision_id", "artifact_version_id"]);
    literal(item.entity_kind, `${path}.entity_kind`, ["decision_evidence"]);
    uuidParser(item.decision_id, `${path}.decision_id`);
    uuidParser(item.artifact_version_id, `${path}.artifact_version_id`);
    return value as HumanDecisionEvidenceRef;
  }
  const kind = literal(discriminant, `${path}.entity_kind`, ["artifact_version", "decision", "journal_entry"]);
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, [kind]);
  uuidParser(item.id, `${path}.id`);
  return value as HumanRootRef;
}

function normalizeRootRef(value: unknown, path: string): HumanRootRef {
  const parsed = parseRootRef(value, path, true);
  return parsed.entity_kind === "decision_evidence"
    ? { ...parsed, decision_id: parsed.decision_id.toLowerCase(), artifact_version_id: parsed.artifact_version_id.toLowerCase() }
    : { ...parsed, id: parsed.id.toLowerCase() };
}

/** Validate request semantics and materialize the server defaults/canonical order. */
export function normalizeHumanProvenanceRequest(
  value: HumanProvenanceRequest,
): NormalizedHumanProvenanceRequest {
  const item = exactRecord(value, "request", ["project_id", "projection"], [
    "root_ref", "record_kinds", "query", "filters", "group_by", "sort", "page_size",
    "cursor", "as_of", "recorded_as_of", "snapshot_token",
  ]);
  const projectId = requestUuid(item.project_id, "request.project_id").toLowerCase();
  const projection = literal(item.projection, "request.projection", PROJECTIONS);
  const rootRef = item.root_ref == null ? null : normalizeRootRef(item.root_ref, "request.root_ref");
  const recordKinds = normalizeEnumList(
    item.record_kinds ?? RECORD_KINDS, "request.record_kinds", RECORD_KINDS, KIND_RANK,
  );
  if (!recordKinds.length) fail("request.record_kinds", "must contain at least one record kind");
  let query: string | null = null;
  if (item.query != null) {
    query = string(item.query, "request.query", 1, 300).trim();
    if (!query) fail("request.query", "must not be blank");
    if (CONTROL_CHARACTER_PATTERN.test(query)) {
      fail("request.query", "must not contain control characters");
    }
  }
  const filters = normalizeFilters(item.filters ?? {}, "request.filters");
  const groupBy = item.group_by == null
    ? null
    : literal(item.group_by, "request.group_by", GROUP_DIMENSIONS);
  literal(item.sort ?? "effective_at_desc", "request.sort", ["effective_at_desc"]);
  const pageSize = integer(item.page_size ?? 50, "request.page_size", 1, 100);
  const cursor = item.cursor == null ? null : string(item.cursor, "request.cursor", 1, 2048);
  const asOf = item.as_of == null ? null : awareTimestamp(item.as_of, "request.as_of");
  const hasRecorded = item.recorded_as_of !== undefined;
  const hasToken = item.snapshot_token !== undefined;
  if (hasRecorded !== hasToken) fail("request", "recorded_as_of and snapshot_token must be supplied together");
  const recordedAsOf = hasRecorded
    ? fixedUtcTimestamp(item.recorded_as_of, "request.recorded_as_of")
    : undefined;
  const token = hasToken ? snapshotToken(item.snapshot_token, "request.snapshot_token") : undefined;
  if ((hasRecorded || cursor) && asOf === null) {
    fail("request.as_of", "is required for snapshot replay or cursor continuation");
  }
  if (cursor && (!recordedAsOf || !token)) {
    fail("request.cursor", "continuation requires as_of, recorded_as_of and snapshot_token");
  }
  if (asOf && recordedAsOf && compareInstantsAtMicrosecondPrecision(asOf, recordedAsOf) > 0) {
    fail("request.as_of", "must not be later than recorded_as_of");
  }
  if (["snapshot", "search", "facet"].includes(projection) && rootRef) {
    fail("request.root_ref", `${projection} does not accept root_ref`);
  }
  if (projection === "related" && rootRef?.entity_kind !== "artifact_version") {
    fail("request.root_ref", "related requires one artifact_version root_ref");
  }
  if (projection === "focus" && (!rootRef || rootRef.entity_kind === "artifact_version")) {
    fail("request.root_ref", "focus requires one human-record root_ref");
  }
  if (projection === "focus" && rootRef && rootRef.entity_kind !== "artifact_version"
    && !recordKinds.includes(rootRef.entity_kind)) {
    fail("request.record_kinds", "focus must include its root kind");
  }
  if (["snapshot", "related", "focus"].includes(projection) && query) {
    fail("request.query", `${projection} does not accept query`);
  }
  if (projection === "search" && query === null && filtersAreEmpty(filters)) {
    fail("request", "search requires query or at least one filter");
  }
  if (projection === "facet" && groupBy === null) fail("request.group_by", "facet requires group_by");
  if (projection !== "facet" && groupBy !== null) fail("request.group_by", "is valid only for facet");
  if (projection === "focus" && !filtersAreEmpty(filters)) {
    fail("request.filters", "focus does not accept discovery filters");
  }
  return {
    project_id: projectId,
    projection,
    root_ref: rootRef,
    record_kinds: recordKinds,
    query,
    filters,
    group_by: groupBy,
    sort: "effective_at_desc",
    page_size: pageSize,
    cursor,
    as_of: asOf,
    ...(recordedAsOf && token ? { recorded_as_of: recordedAsOf, snapshot_token: token } : {}),
  };
}

function validateRef(value: unknown, path: string, kind: "artifact_version" | "decision" | "journal_entry" | "decision_status_event" | "operation_run"): void {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, [kind]);
  documentUuid(item.id, `${path}.id`);
}

function validateSensitivity(value: unknown, path: string, basis: HumanSensitivityView["basis"]): void {
  const item = exactRecord(value, path, ["basis", "tier_id", "tier_name"]);
  literal(item.basis, `${path}.basis`, [basis]);
  documentUuid(item.tier_id, `${path}.tier_id`);
  string(item.tier_name, `${path}.tier_name`, 1, 100);
}

function validateValidity(value: unknown, path: string, basis: HumanValidityView["basis"]): void {
  const item = exactRecord(value, path, [
    "basis", "lifecycle_state", "considered_valid_at_time", "authoritative_for_new_work_at_time",
  ]);
  literal(item.basis, `${path}.basis`, [basis]);
  const state = literal(item.lifecycle_state, `${path}.lifecycle_state`, LIFECYCLE_STATES);
  const considered = bool(item.considered_valid_at_time, `${path}.considered_valid_at_time`);
  const authoritative = bool(item.authoritative_for_new_work_at_time, `${path}.authoritative_for_new_work_at_time`);
  const invalid = state !== "active";
  if (considered !== !invalid) fail(`${path}.considered_valid_at_time`, "must match lifecycle_state");
  if (authoritative !== (state === "active")) fail(`${path}.authoritative_for_new_work_at_time`, "must match lifecycle_state");
}

const DECISION_CARD_KEYS = [
  "entity_kind", "ref", "project_id", "decision_key", "technical_claim_ref",
  "technical_claim_name", "current_status", "current_status_event_ref", "creator_actor_id",
  "sensitivity", "validity", "effective_at", "recorded_at",
] as const;
const EVIDENCE_CARD_KEYS = [
  "entity_kind", "ref", "project_id", "decision_ref", "evidence_artifact_version_ref",
  "evidence_name", "decision_status", "evidence_type", "creator_actor_id", "sensitivity",
  "validity", "effective_at", "recorded_at",
] as const;
const JOURNAL_CARD_KEYS = [
  "entity_kind", "ref", "project_id", "subject_ref", "content_artifact_version_ref",
  "content_name", "decision_ref", "entry_type", "author_actor_id", "publication_operation_ref",
  "sensitivity", "validity", "effective_at", "recorded_at",
] as const;

function validateDecisionCard(value: unknown, path: string, extra: readonly string[] = []): HumanDecisionCard {
  const item = exactRecord(value, path, [...DECISION_CARD_KEYS, ...extra]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision"]);
  validateRef(item.ref, `${path}.ref`, "decision");
  documentUuid(item.project_id, `${path}.project_id`);
  string(item.decision_key, `${path}.decision_key`, 1, 200);
  validateRef(item.technical_claim_ref, `${path}.technical_claim_ref`, "artifact_version");
  string(item.technical_claim_name, `${path}.technical_claim_name`, 1, 300);
  literal(item.current_status, `${path}.current_status`, DECISION_STATUSES);
  validateRef(item.current_status_event_ref, `${path}.current_status_event_ref`, "decision_status_event");
  documentUuid(item.creator_actor_id, `${path}.creator_actor_id`);
  validateSensitivity(item.sensitivity, `${path}.sensitivity`, "technical_claim_artifact");
  validateValidity(item.validity, `${path}.validity`, "technical_claim_version");
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  return value as HumanDecisionCard;
}

function validateEvidenceCard(value: unknown, path: string, extra: readonly string[] = []): HumanEvidenceCard {
  const item = exactRecord(value, path, [...EVIDENCE_CARD_KEYS, ...extra]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision_evidence"]);
  const ref = parseRootRef(item.ref, `${path}.ref`) as HumanDecisionEvidenceRef;
  if (ref.entity_kind !== "decision_evidence") fail(`${path}.ref.entity_kind`, "expected decision_evidence");
  validateRef(item.decision_ref, `${path}.decision_ref`, "decision");
  validateRef(item.evidence_artifact_version_ref, `${path}.evidence_artifact_version_ref`, "artifact_version");
  const decisionRef = item.decision_ref as HumanDecisionRef;
  const evidenceRef = item.evidence_artifact_version_ref as HumanArtifactVersionRef;
  if (ref.decision_id !== decisionRef.id) fail(`${path}.ref.decision_id`, "must match decision_ref.id");
  if (ref.artifact_version_id !== evidenceRef.id) {
    fail(`${path}.ref.artifact_version_id`, "must match evidence_artifact_version_ref.id");
  }
  documentUuid(item.project_id, `${path}.project_id`);
  string(item.evidence_name, `${path}.evidence_name`, 1, 300);
  literal(item.decision_status, `${path}.decision_status`, DECISION_STATUSES);
  const evidenceType = exactRecord(item.evidence_type, `${path}.evidence_type`, ["basis", "value"]);
  literal(evidenceType.basis, `${path}.evidence_type.basis`, ["artifact_kind"]);
  const evidenceTypeValue = string(evidenceType.value, `${path}.evidence_type.value`, 1, 100);
  if (!HUMAN_OUTPUT_TYPE_PATTERN.test(evidenceTypeValue)) {
    fail(`${path}.evidence_type.value`, "expected a canonical lowercase identifier");
  }
  documentUuid(item.creator_actor_id, `${path}.creator_actor_id`);
  validateSensitivity(item.sensitivity, `${path}.sensitivity`, "evidence_artifact");
  validateValidity(item.validity, `${path}.validity`, "evidence_artifact_version");
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  return value as HumanEvidenceCard;
}

function validateJournalCard(value: unknown, path: string, extra: readonly string[] = []): HumanJournalCard {
  const item = exactRecord(value, path, [...JOURNAL_CARD_KEYS, ...extra]);
  literal(item.entity_kind, `${path}.entity_kind`, ["journal_entry"]);
  validateRef(item.ref, `${path}.ref`, "journal_entry");
  documentUuid(item.project_id, `${path}.project_id`);
  validateRef(item.subject_ref, `${path}.subject_ref`, "artifact_version");
  validateRef(item.content_artifact_version_ref, `${path}.content_artifact_version_ref`, "artifact_version");
  string(item.content_name, `${path}.content_name`, 1, 300);
  if (item.decision_ref !== null) validateRef(item.decision_ref, `${path}.decision_ref`, "decision");
  const entryType = string(item.entry_type, `${path}.entry_type`, 1, 100);
  if (!HUMAN_OUTPUT_TYPE_PATTERN.test(entryType)) {
    fail(`${path}.entry_type`, "expected a canonical lowercase identifier");
  }
  documentUuid(item.author_actor_id, `${path}.author_actor_id`);
  validateRef(item.publication_operation_ref, `${path}.publication_operation_ref`, "operation_run");
  validateSensitivity(item.sensitivity, `${path}.sensitivity`, "journal_content_artifact");
  validateValidity(item.validity, `${path}.validity`, "journal_content_artifact_version");
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  return value as HumanJournalCard;
}

function validateCard(value: unknown, path: string): HumanCard {
  const kind = record(value, path).entity_kind;
  if (kind === "decision") return validateDecisionCard(value, path);
  if (kind === "decision_evidence") return validateEvidenceCard(value, path);
  if (kind === "journal_entry") return validateJournalCard(value, path);
  fail(`${path}.entity_kind`, "expected decision, decision_evidence, or journal_entry");
}

function validateStatusEvent(value: unknown, path: string): HumanDecisionStatusEvent {
  const item = exactRecord(value, path, [
    "entity_kind", "ref", "decision_ref", "status", "actor_id", "effective_at", "recorded_at",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision_status_event"]);
  validateRef(item.ref, `${path}.ref`, "decision_status_event");
  validateRef(item.decision_ref, `${path}.decision_ref`, "decision");
  literal(item.status, `${path}.status`, DECISION_STATUSES);
  documentUuid(item.actor_id, `${path}.actor_id`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  return value as HumanDecisionStatusEvent;
}

function validateOmissionMarker(value: unknown, path: string): HumanEvidenceOmissionMarker {
  const item = exactRecord(value, path, ["status", "marker"]);
  literal(item.status, `${path}.status`, ["incomplete"]);
  literal(item.marker, `${path}.marker`, ["restricted_evidence_omitted"]);
  return value as HumanEvidenceOmissionMarker;
}

function validateDecisionEvidence(value: unknown, path: string, decisionId: string): HumanDecisionEvidence {
  const item = exactRecord(value, path, ["disclosure", "items", "marker"]);
  const disclosure = literal(item.disclosure, `${path}.disclosure`, ["complete", "partial"]);
  const items = array(item.items, `${path}.items`, HUMAN_PROVENANCE_LIMITS.maxEvidence);
  const identities: string[] = [];
  if (disclosure === "complete") {
    if (item.marker !== null) fail(`${path}.marker`, "complete evidence requires null marker");
    let previousOrdinal = -1;
    items.forEach((entry, index) => {
      const evidence = validateEvidenceCard(entry, `${path}.items[${index}]`, ["ordinal"]);
      const ordinal = integer(record(entry, `${path}.items[${index}]`).ordinal, `${path}.items[${index}].ordinal`, 0);
      if (ordinal <= previousOrdinal) fail(`${path}.items`, "complete evidence must preserve unique stored-ordinal order");
      previousOrdinal = ordinal;
      if (evidence.decision_ref.id !== decisionId) fail(`${path}.items[${index}].decision_ref.id`, "must match decision");
      identities.push(`${evidence.ref.decision_id}:${evidence.ref.artifact_version_id}`);
    });
  } else {
    validateOmissionMarker(item.marker, `${path}.marker`);
    items.forEach((entry, index) => {
      const evidence = validateEvidenceCard(entry, `${path}.items[${index}]`);
      if (evidence.decision_ref.id !== decisionId) fail(`${path}.items[${index}].decision_ref.id`, "must match decision");
      identities.push(`${evidence.ref.decision_id}:${evidence.ref.artifact_version_id}`);
    });
  }
  unique(identities, `${path}.items`);
  return value as HumanDecisionEvidence;
}

function validateDetail(value: unknown, path: string): HumanDetail {
  const kind = record(value, path).entity_kind;
  if (kind === "decision") {
    const detail = validateDecisionCard(value, path, ["status_history", "evidence"]) as HumanDecisionDetail;
    const history = array(record(value, path).status_history, `${path}.status_history`, HUMAN_PROVENANCE_LIMITS.maxStatusEvents, 1)
      .map((entry, index) => validateStatusEvent(entry, `${path}.status_history[${index}]`));
    const ids = history.map((event) => event.ref.id);
    unique(ids, `${path}.status_history`);
    history.forEach((event, index) => {
      if (event.decision_ref.id !== detail.ref.id) fail(`${path}.status_history[${index}].decision_ref.id`, "must match decision");
      if (index > 0 && compareInstantsAtMicrosecondPrecision(history[index - 1]!.effective_at, event.effective_at) > 0) {
        fail(`${path}.status_history`, "must use nondecreasing effective-time order");
      }
    });
    const current = history[history.length - 1]!;
    if (current.ref.id !== detail.current_status_event_ref.id) {
      fail(`${path}.current_status_event_ref`, "must identify the final status history event");
    }
    if (current.status !== detail.current_status) fail(`${path}.current_status`, "must equal the final status history event");
    validateDecisionEvidence(record(value, path).evidence, `${path}.evidence`, detail.ref.id);
    const decisionEvidence = record(value, path).evidence as HumanDecisionEvidence;
    decisionEvidence.items.forEach((evidence, index) => {
      if (evidence.decision_status !== detail.current_status) {
        fail(`${path}.evidence.items[${index}].decision_status`, "must equal parent current_status");
      }
    });
    return value as HumanDecisionDetail;
  }
  if (kind === "decision_evidence") {
    const detail = validateEvidenceCard(value, path, [
      "parent_decision", "parent_evidence_disclosure", "ordinal", "marker",
    ]) as HumanEvidenceDetail;
    const item = record(value, path);
    const parent = validateDecisionCard(item.parent_decision, `${path}.parent_decision`);
    if (parent.ref.id !== detail.decision_ref.id) fail(`${path}.parent_decision.ref.id`, "must match decision_ref");
    if (detail.decision_status !== parent.current_status) {
      fail(`${path}.decision_status`, "must equal parent_decision.current_status");
    }
    const disclosure = literal(
      item.parent_evidence_disclosure, `${path}.parent_evidence_disclosure`, ["complete", "partial"],
    );
    if (disclosure === "complete") {
      integer(item.ordinal, `${path}.ordinal`, 0);
      if (item.marker !== null) fail(`${path}.marker`, "must be null for complete evidence");
    } else {
      if (item.ordinal !== null) fail(`${path}.ordinal`, "must be null for partial evidence");
      validateOmissionMarker(item.marker, `${path}.marker`);
    }
    return value as HumanEvidenceDetail;
  }
  if (kind === "journal_entry") {
    const detail = validateJournalCard(value, path, ["related_decision"]) as HumanJournalDetail;
    const related = record(value, path).related_decision;
    if ((detail.decision_ref === null) !== (related === null)) {
      fail(`${path}.related_decision`, "must be present or absent atomically with decision_ref");
    }
    if (related !== null) {
      const decision = validateDecisionCard(related, `${path}.related_decision`);
      if (decision.ref.id !== detail.decision_ref?.id) fail(`${path}.related_decision.ref.id`, "must match decision_ref");
    }
    return value as HumanJournalDetail;
  }
  fail(`${path}.entity_kind`, "expected a human detail kind");
}

function parseAppliedFilters(value: unknown, path: string): HumanAppliedFilters {
  const item = exactRecord(value, path, [
    "actor_ids", "decision_statuses", "evidence_types", "entry_types", "sensitivity_tier_ids",
    "lifecycle_states", "effective_from", "effective_through",
  ]);
  const actorIds = array(item.actor_ids, `${path}.actor_ids`, 50)
    .map((entry, index) => documentUuid(entry, `${path}.actor_ids[${index}]`));
  const statuses = array(item.decision_statuses, `${path}.decision_statuses`, 6)
    .map((entry, index) => literal(entry, `${path}.decision_statuses[${index}]`, DECISION_STATUSES));
  const evidenceTypes = array(item.evidence_types, `${path}.evidence_types`, 50)
    .map((entry, index) => {
      const value = string(entry, `${path}.evidence_types[${index}]`, 1, 100);
      if (!HUMAN_OUTPUT_TYPE_PATTERN.test(value)) fail(`${path}.evidence_types[${index}]`, "must use a canonical type identifier");
      return value;
    });
  const entryTypes = array(item.entry_types, `${path}.entry_types`, 50)
    .map((entry, index) => {
      const value = string(entry, `${path}.entry_types[${index}]`, 1, 100);
      if (!HUMAN_OUTPUT_TYPE_PATTERN.test(value)) fail(`${path}.entry_types[${index}]`, "must use a canonical type identifier");
      return value;
    });
  const tierIds = array(item.sensitivity_tier_ids, `${path}.sensitivity_tier_ids`, 50)
    .map((entry, index) => documentUuid(entry, `${path}.sensitivity_tier_ids[${index}]`));
  const lifecycle = array(item.lifecycle_states, `${path}.lifecycle_states`, 6)
    .map((entry, index) => literal(entry, `${path}.lifecycle_states[${index}]`, LIFECYCLE_STATES));
  for (const [values, field] of [
    [actorIds, "actor_ids"], [statuses, "decision_statuses"], [evidenceTypes, "evidence_types"],
    [entryTypes, "entry_types"], [tierIds, "sensitivity_tier_ids"], [lifecycle, "lifecycle_states"],
  ] as const) unique(values, `${path}.${field}`);
  if (!sameStrings(actorIds, [...actorIds].sort())) fail(`${path}.actor_ids`, "must use canonical order");
  if (!sameStrings(tierIds, [...tierIds].sort())) fail(`${path}.sensitivity_tier_ids`, "must use canonical order");
  if (!sameStrings(statuses, [...statuses].sort((a, b) => STATUS_RANK[a] - STATUS_RANK[b]))) {
    fail(`${path}.decision_statuses`, "must use canonical order");
  }
  if (!sameStrings(lifecycle, [...lifecycle].sort((a, b) => LIFECYCLE_RANK[a] - LIFECYCLE_RANK[b]))) {
    fail(`${path}.lifecycle_states`, "must use canonical order");
  }
  if (!sameStrings(evidenceTypes, [...evidenceTypes].sort())) fail(`${path}.evidence_types`, "must use canonical order");
  if (!sameStrings(entryTypes, [...entryTypes].sort())) fail(`${path}.entry_types`, "must use canonical order");
  const effectiveFrom = item.effective_from === null ? null : fixedUtcTimestamp(item.effective_from, `${path}.effective_from`);
  const effectiveThrough = item.effective_through === null ? null : fixedUtcTimestamp(item.effective_through, `${path}.effective_through`);
  if (effectiveFrom && effectiveThrough && compareInstantsAtMicrosecondPrecision(effectiveFrom, effectiveThrough) > 0) {
    fail(path, "effective_from must not follow effective_through");
  }
  return value as HumanAppliedFilters;
}

function parseAppliedSelection(value: unknown, path: string): HumanAppliedSelection {
  const item = exactRecord(value, path, ["record_kinds", "query", "filters", "group_by", "sort", "page_size"]);
  const kinds = array(item.record_kinds, `${path}.record_kinds`, 3, 1)
    .map((entry, index) => literal(entry, `${path}.record_kinds[${index}]`, RECORD_KINDS));
  unique(kinds, `${path}.record_kinds`);
  if (!sameStrings(kinds, [...kinds].sort((a, b) => KIND_RANK[a] - KIND_RANK[b]))) {
    fail(`${path}.record_kinds`, "must use canonical order");
  }
  if (item.query !== null) {
    const query = string(item.query, `${path}.query`, 1, 300);
    if (query !== query.trim()) fail(`${path}.query`, "must be trimmed");
    if (CONTROL_CHARACTER_PATTERN.test(query)) fail(`${path}.query`, "must not contain control characters");
  }
  parseAppliedFilters(item.filters, `${path}.filters`);
  if (item.group_by !== null) literal(item.group_by, `${path}.group_by`, GROUP_DIMENSIONS);
  literal(item.sort, `${path}.sort`, ["effective_at_desc"]);
  integer(item.page_size, `${path}.page_size`, 1, 100);
  return value as HumanAppliedSelection;
}

function cardIdentity(card: HumanCard): string {
  return card.entity_kind === "decision_evidence"
    ? `decision_evidence:${card.ref.decision_id}:${card.ref.artifact_version_id}`
    : `${card.entity_kind}:${card.ref.id}`;
}

export function compareHumanCardsCanonical(left: HumanCard, right: HumanCard): number {
  if (left.effective_at !== right.effective_at) return right.effective_at.localeCompare(left.effective_at);
  const kindOrder = KIND_RANK[left.entity_kind] - KIND_RANK[right.entity_kind];
  if (kindOrder) return kindOrder;
  const leftPrimary = left.entity_kind === "decision_evidence" ? left.ref.decision_id : left.ref.id;
  const rightPrimary = right.entity_kind === "decision_evidence" ? right.ref.decision_id : right.ref.id;
  if (leftPrimary !== rightPrimary) return rightPrimary.localeCompare(leftPrimary);
  if (left.entity_kind === "decision_evidence" && right.entity_kind === "decision_evidence") {
    return right.ref.artifact_version_id.localeCompare(left.ref.artifact_version_id);
  }
  return 0;
}

function collectDetailCards(detail: HumanDetail | null): { cards: HumanCard[]; events: HumanDecisionStatusEvent[] } {
  if (!detail) return { cards: [], events: [] };
  if (detail.entity_kind === "decision") {
    return { cards: [detail, ...detail.evidence.items], events: detail.status_history };
  }
  if (detail.entity_kind === "decision_evidence") {
    return { cards: [detail, detail.parent_decision], events: [] };
  }
  return { cards: [detail, ...(detail.related_decision ? [detail.related_decision] : [])], events: [] };
}

function sameRoot(left: HumanRootRef | null, right: HumanRootRef | null): boolean {
  if (left === null || right === null) return left === right;
  if (left.entity_kind !== right.entity_kind) return false;
  if (left.entity_kind === "decision_evidence" && right.entity_kind === "decision_evidence") {
    return left.decision_id === right.decision_id && left.artifact_version_id === right.artifact_version_id;
  }
  return left.entity_kind !== "decision_evidence" && right.entity_kind !== "decision_evidence" && left.id === right.id;
}

function assertDocumentSize(value: unknown): void {
  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    fail("$.data", "expected an acyclic JSON document");
  }
  if (new TextEncoder().encode(serialized).byteLength > HUMAN_PROVENANCE_LIMITS.maxDocumentCanonicalBytes) {
    fail("$.data", "human provenance document exceeds canonical byte cap");
  }
}

function parseHumanProvenanceResult(value: unknown, path: string): HumanProvenanceResult {
  const item = exactRecord(value, path, [
    "contract_version", "resource_limits_version", "project_id", "projection", "temporal_cut",
    "root_ref", "applied_selection", "items", "detail", "buckets", "next_cursor", "truncated",
    "temporal_history", "warnings",
  ]);
  literal(item.contract_version, `${path}.contract_version`, [HUMAN_PROVENANCE_CONTRACT_VERSION]);
  literal(item.resource_limits_version, `${path}.resource_limits_version`, [HUMAN_PROVENANCE_RESOURCE_LIMITS_VERSION]);
  const projectId = documentUuid(item.project_id, `${path}.project_id`);
  const projection = literal(item.projection, `${path}.projection`, PROJECTIONS);
  const cut = exactRecord(item.temporal_cut, `${path}.temporal_cut`, [
    "effective_as_of", "recorded_as_of", "snapshot_token",
  ]);
  const effectiveAsOf = fixedUtcTimestamp(cut.effective_as_of, `${path}.temporal_cut.effective_as_of`);
  const recordedAsOf = fixedUtcTimestamp(cut.recorded_as_of, `${path}.temporal_cut.recorded_as_of`);
  snapshotToken(cut.snapshot_token, `${path}.temporal_cut.snapshot_token`);
  if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
    fail(`${path}.temporal_cut.effective_as_of`, "must not be later than recorded_as_of");
  }
  const rootRef = item.root_ref === null ? null : parseRootRef(item.root_ref, `${path}.root_ref`);
  const selection = parseAppliedSelection(item.applied_selection, `${path}.applied_selection`);
  const items = array(item.items, `${path}.items`, 100)
    .map((entry, index) => validateCard(entry, `${path}.items[${index}]`));
  const detail = item.detail === null ? null : validateDetail(item.detail, `${path}.detail`);
  const buckets = array(item.buckets, `${path}.buckets`, 100).map((entry, index) => {
    const bucketPath = `${path}.buckets[${index}]`;
    const bucket = exactRecord(entry, bucketPath, ["dimension", "key", "label", "count"]);
    literal(bucket.dimension, `${bucketPath}.dimension`, GROUP_DIMENSIONS);
    string(bucket.key, `${bucketPath}.key`, 1, 300);
    string(bucket.label, `${bucketPath}.label`, 1, 300);
    integer(bucket.count, `${bucketPath}.count`, 1);
    return entry as HumanFacetBucket;
  });
  const nextCursor = item.next_cursor === null ? null : string(item.next_cursor, `${path}.next_cursor`, 1, 2048);
  const truncated = bool(item.truncated, `${path}.truncated`);
  const temporal = exactRecord(item.temporal_history, `${path}.temporal_history`, ["status", "marker"]);
  const temporalStatus = literal(temporal.status, `${path}.temporal_history.status`, ["complete", "incomplete"]);
  if (temporalStatus === "complete") {
    if (temporal.marker !== null) fail(`${path}.temporal_history.marker`, "must be null when complete");
  } else {
    literal(temporal.marker, `${path}.temporal_history.marker`, ["temporal_history_incomplete"]);
  }
  const warnings = array(item.warnings, `${path}.warnings`, 100)
    .map((entry, index) => literal(entry, `${path}.warnings[${index}]`, WARNINGS));

  if (["snapshot", "search", "related"].includes(projection)) {
    if (detail !== null || buckets.length) fail(path, "list projection must return only items");
  } else if (projection === "focus") {
    if (!rootRef || !detail || items.length || buckets.length) fail(path, "focus must return exactly one detail");
  } else if (items.length || detail !== null) fail(path, "facet must return only buckets");
  if (["snapshot", "search", "facet"].includes(projection) && rootRef) {
    fail(`${path}.root_ref`, `${projection} result cannot carry root_ref`);
  }
  if (projection === "related" && rootRef?.entity_kind !== "artifact_version") {
    fail(`${path}.root_ref`, "related result requires its artifact_version root");
  }
  if (projection === "focus" && (!rootRef || rootRef.entity_kind === "artifact_version")) {
    fail(`${path}.root_ref`, "focus result requires its human-record root");
  }
  if (projection === "focus" && detail && !selection.record_kinds.includes(detail.entity_kind)) {
    fail(`${path}.detail.entity_kind`, "must be admitted by applied record_kinds");
  }
  if (["snapshot", "related", "focus"].includes(projection) && selection.query) {
    fail(`${path}.applied_selection.query`, `${projection} cannot echo a query`);
  }
  if (projection === "facet" && selection.group_by === null) {
    fail(`${path}.applied_selection.group_by`, "facet requires applied group_by");
  }
  if (projection !== "facet" && selection.group_by !== null) {
    fail(`${path}.applied_selection.group_by`, "only facet may echo group_by");
  }
  if ((nextCursor !== null) !== truncated) fail(`${path}.next_cursor`, "must be present exactly when truncated");
  if (projection === "focus" && truncated) fail(`${path}.truncated`, "focus semantic units are never paginated");
  const pageWarning = warnings.includes("human_provenance_page_truncated");
  const temporalWarning = warnings.includes("temporal_history_incomplete");
  if (pageWarning !== truncated) fail(`${path}.warnings`, "page truncation warning must match truncated");
  if (temporalWarning !== (temporalStatus === "incomplete")) {
    fail(`${path}.warnings`, "temporal warning must match temporal_history");
  }
  unique(warnings, `${path}.warnings`);
  const canonicalWarnings = [...warnings].sort((a, b) => WARNINGS.indexOf(a) - WARNINGS.indexOf(b));
  if (!sameStrings(warnings, canonicalWarnings)) fail(`${path}.warnings`, "must use canonical order");
  const bucketDimensions = new Set(buckets.map((bucket) => bucket.dimension));
  if (bucketDimensions.size > 1) fail(`${path}.buckets`, "one facet page cannot mix dimensions");
  if (buckets.length && bucketDimensions.values().next().value !== selection.group_by) {
    fail(`${path}.buckets`, "must match applied group_by");
  }
  const bucketKeys = buckets.map((bucket) => bucket.key);
  unique(bucketKeys, `${path}.buckets`);
  if (!sameStrings(bucketKeys, [...bucketKeys].sort())) fail(`${path}.buckets`, "keys must use canonical order");
  const identities = items.map(cardIdentity);
  unique(identities, `${path}.items`);
  items.forEach((card, index) => {
    if (!selection.record_kinds.includes(card.entity_kind)) {
      fail(`${path}.items[${index}].entity_kind`, "is outside applied record_kinds");
    }
  });
  const sortedItems = [...items].sort(compareHumanCardsCanonical);
  if (!sameStrings(items.map(cardIdentity), sortedItems.map(cardIdentity))) {
    fail(`${path}.items`, "must use canonical effective-time order");
  }
  const detailRecords = collectDetailCards(detail);
  [...items, ...detailRecords.cards].forEach((card, index) => {
    if (card.project_id !== projectId) fail(`${path}.records[${index}].project_id`, "must match result project_id");
    if (compareInstantsAtMicrosecondPrecision(card.effective_at, effectiveAsOf) > 0) {
      fail(`${path}.records[${index}].effective_at`, "exceeds effective temporal cut");
    }
    if (compareInstantsAtMicrosecondPrecision(card.recorded_at, recordedAsOf) > 0) {
      fail(`${path}.records[${index}].recorded_at`, "exceeds recorded temporal cut");
    }
  });
  detailRecords.events.forEach((event, index) => {
    if (compareInstantsAtMicrosecondPrecision(event.effective_at, effectiveAsOf) > 0) {
      fail(`${path}.detail.status_history[${index}].effective_at`, "exceeds effective temporal cut");
    }
    if (compareInstantsAtMicrosecondPrecision(event.recorded_at, recordedAsOf) > 0) {
      fail(`${path}.detail.status_history[${index}].recorded_at`, "exceeds recorded temporal cut");
    }
  });
  if (rootRef?.entity_kind === "decision") {
    if (detail?.entity_kind !== "decision" || detail.ref.id !== rootRef.id) fail(`${path}.detail`, "must equal decision focus root");
  } else if (rootRef?.entity_kind === "decision_evidence") {
    if (detail?.entity_kind !== "decision_evidence"
      || detail.ref.decision_id !== rootRef.decision_id
      || detail.ref.artifact_version_id !== rootRef.artifact_version_id) {
      fail(`${path}.detail`, "must equal evidence focus root");
    }
  } else if (rootRef?.entity_kind === "journal_entry") {
    if (detail?.entity_kind !== "journal_entry" || detail.ref.id !== rootRef.id) fail(`${path}.detail`, "must equal journal focus root");
  }
  assertDocumentSize(value);
  return value as HumanProvenanceResult;
}

function expectedSelection(request: NormalizedHumanProvenanceRequest): HumanAppliedSelection {
  return {
    record_kinds: request.record_kinds,
    query: request.query,
    filters: request.filters,
    group_by: request.group_by,
    sort: request.sort,
    page_size: request.page_size,
  };
}

function stableJson(value: unknown): string {
  return JSON.stringify(value);
}

export function parseHumanProvenanceEnvelope(
  value: unknown,
  request?: HumanProvenanceRequest | NormalizedHumanProvenanceRequest,
): HumanProvenanceEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  documentUuid(item.request_id, "$.request_id");
  const data = parseHumanProvenanceResult(item.data, "$.data");
  const page = exactRecord(item.page, "$.page", ["next_cursor", "truncated"]);
  const pageCursor = page.next_cursor === null ? null : string(page.next_cursor, "$.page.next_cursor", 1, 2048);
  const pageTruncated = bool(page.truncated, "$.page.truncated");
  if (pageCursor !== data.next_cursor || pageTruncated !== data.truncated) fail("$.page", "must mirror data pagination");
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [HUMAN_PROVENANCE_CONTRACT_VERSION]);
  const schemaMigration = string(
    provenance.schema_migration,
    "$.provenance.schema_migration",
  );
  if (!/^\d{14}$/.test(schemaMigration)
    || schemaMigration < HUMAN_PROVENANCE_MIN_SCHEMA_MIGRATION) {
    fail(
      "$.provenance.schema_migration",
      `expected a migration marker at or after ${HUMAN_PROVENANCE_MIN_SCHEMA_MIGRATION}`,
    );
  }
  const outerWarnings = array(item.warnings, "$.warnings", 100)
    .map((entry, index) => literal(entry, `$.warnings[${index}]`, WARNINGS));
  if (!sameStrings(outerWarnings, data.warnings)) fail("$.warnings", "must mirror data.warnings");
  if (request) {
    const normalized = normalizeHumanProvenanceRequest(request);
    if (data.project_id !== normalized.project_id) fail("$.data.project_id", "must equal request.project_id");
    if (data.projection !== normalized.projection) fail("$.data.projection", "must equal request.projection");
    if (!sameRoot(data.root_ref, normalized.root_ref)) fail("$.data.root_ref", "must equal request.root_ref");
    if (stableJson(data.applied_selection) !== stableJson(expectedSelection(normalized))) {
      fail("$.data.applied_selection", "must exactly echo the normalized request selection");
    }
    if (normalized.as_of !== null
      && compareInstantsAtMicrosecondPrecision(data.temporal_cut.effective_as_of, normalized.as_of) !== 0) {
      fail("$.data.temporal_cut.effective_as_of", "must equal request.as_of instant");
    }
    if (normalized.recorded_as_of !== undefined
      && data.temporal_cut.recorded_as_of !== normalized.recorded_as_of) {
      fail("$.data.temporal_cut.recorded_as_of", "must equal request.recorded_as_of");
    }
    if (normalized.snapshot_token !== undefined
      && data.temporal_cut.snapshot_token !== normalized.snapshot_token) {
      fail("$.data.temporal_cut.snapshot_token", "must equal request.snapshot_token");
    }
  }
  return value as HumanProvenanceEnvelope;
}

export function humanEvidenceKey(ref: HumanDecisionEvidenceRef): string {
  return `decision_evidence:${ref.decision_id}:${ref.artifact_version_id}`;
}

/** Continue from a page without changing any semantic selector or temporal cut. */
export function bindHumanProvenanceContinuation(
  request: HumanProvenanceRequest | NormalizedHumanProvenanceRequest,
  page: HumanProvenanceEnvelope,
): HumanProvenanceRequest {
  const normalized = normalizeHumanProvenanceRequest(request);
  if (!page.data.next_cursor) fail("page.data.next_cursor", "cannot continue a terminal page");
  return {
    ...normalized,
    cursor: page.data.next_cursor,
    as_of: page.data.temporal_cut.effective_as_of,
    recorded_as_of: page.data.temporal_cut.recorded_as_of,
    snapshot_token: page.data.temporal_cut.snapshot_token,
  };
}

export async function fetchHumanProvenance(
  request: HumanProvenanceRequest,
  options: { signal?: AbortSignal } = {},
): Promise<HumanProvenanceEnvelope> {
  const normalized = normalizeHumanProvenanceRequest(request);
  const response = await apiClient<unknown>(HUMAN_PROVENANCE_PATH, {
    method: "POST",
    body: normalized,
    signal: options.signal,
  });
  return parseHumanProvenanceEnvelope(response, normalized);
}
